import {
  FetchPolicy,
  gql,
  useApolloClient,
  useQuery,
  WatchQueryFetchPolicy,
} from "@apollo/client";
import { getFetchPolicyOptions } from "../utils";
import { useKeycloak } from "@/providers/KeycloakProvider";

export const SAVED_QUERY = gql`
  query GetSavedQuery($queryName: String, $customParameters: Json) {
    savedQuery(queryName: $queryName) {
      id
      name
      queryResult(queryParameters: { customParameters: $customParameters }) {
        queryResultJson
      }
    }
  }
`;

interface SavedQuery<T> {
  savedQuery: {
    id: string;
    name: string;
    queryResult: {
      queryResultJson: T[];
    };
  };
}

export const useGetSavedQuery = () => {
  const client = useApolloClient();
  const { linkedContact, access } = useKeycloak();
  const advisorId =
    access.isAdmin || access.isBackoffice ? "admin" : linkedContact || "";

  const fetchQueryResults = async <T>(
    queryName: string,
    customParameters?: Record<string, unknown>,
    _fetchPolicy: FetchPolicy = "cache-first"
  ) => {
    try {
      const { data, loading, networkStatus, error } = await client.query<
        SavedQuery<T>
      >({
        query: SAVED_QUERY,
        notifyOnNetworkStatusChange: true,
        variables: {
          queryName,
          customParameters: { ...customParameters, advisorId },
        },
        fetchPolicy: _fetchPolicy,
      });
      return { data, networkStatus, loading, error };
    } catch (error) {
      return {
        data: undefined,
        networkStatus: undefined,
        loading: false,
        error: error as Error,
      };
    }
  };

  return { fetchQueryResults };
};

export const useGetSavedQuery2 = <T>(
  queryName: string,
  customParameters?: Record<string, unknown>,
  skip = false,
  fetchPolicy?: WatchQueryFetchPolicy | undefined,
  fetchAll?: boolean
) => {
  const { linkedContact, access } = useKeycloak();
  const advisorId = fetchAll
    ? "admin"
    : access.isAdmin || access.isBackoffice
    ? "admin"
    : linkedContact || "";

  const { loading, error, data, refetch, networkStatus } = useQuery<
    SavedQuery<T>
  >(SAVED_QUERY, {
    variables: {
      queryName,
      customParameters: { ...customParameters, advisorId },
    },
    notifyOnNetworkStatusChange: true,
    skip: skip,
    ...(fetchPolicy
      ? { fetchPolicy }
      : getFetchPolicyOptions(
          `useGetSavedQuery.${queryName}.${JSON.stringify(customParameters)}`
        )),
  });

  return {
    loading: loading,
    error: error,
    data: (data && data.savedQuery?.queryResult?.queryResultJson) || [],
    refetch,
    networkStatus,
  };
};
